import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const categoryGroups = [
    {
      title: 'Abbigliamento',
      items: categories.filter((c) => [911, 912, 913, 914, 915, 919].includes(c.id)),
    },
    {
      title: 'Accessori e Altro',
      items: categories.filter((c) => [916, 920, 921, 922, 923].includes(c.id)),
    },
    {
      title: 'Altro',
      items: categories.filter((c) => [917, 918, 924].includes(c.id)),
    },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            className="flex-shrink-0 font-display text-xl md:text-2xl font-bold tracking-tight text-warm-900"
          >
            ITAOBUY <span className="text-tealwarm-600">SPREAD</span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-warm-700 hover:text-warm-900 transition-colors whitespace-nowrap cursor-pointer">Home</Link>
            <Link to="/#categorie" onClick={(e) => { e.preventDefault(); window.location.href = '/#categorie'; }} className="text-sm font-medium text-warm-700 hover:text-warm-900 transition-colors whitespace-nowrap cursor-pointer">Categorie</Link>
            <Link to="/itaobuy-spreadsheet-guida-completa" className="text-sm font-medium text-warm-700 hover:text-warm-900 transition-colors whitespace-nowrap cursor-pointer">Guida Acquisti</Link>
            <Link to="/#faq" onClick={(e) => { e.preventDefault(); window.location.href = '/#faq'; }} className="text-sm font-medium text-warm-700 hover:text-warm-900 transition-colors whitespace-nowrap cursor-pointer">FAQ</Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-warm-600 text-white text-sm font-semibold hover:bg-warm-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Itaobuy Sheet
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-warm-900 text-white text-sm font-semibold hover:bg-warm-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-warm-900 cursor-pointer bg-transparent border-none"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        <div
          ref={dropdownRef}
          className="hidden md:block relative border-t border-warm-200/40"
        >
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="w-full flex items-center justify-between py-2.5 text-sm text-warm-600 hover:text-warm-900 transition-colors cursor-pointer bg-transparent border-none"
          >
            <span className="flex items-center gap-2">
              <i className="ri-grid-line"></i>
              Esplora le categorie prodotto
            </span>
            <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
          </button>
          {dropdownOpen && (
            <div className="absolute left-0 right-0 top-full bg-white rounded-b-2xl shadow-lg border border-warm-100 z-40 py-2 grid grid-cols-4 gap-1 px-3">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-warm-700 hover:bg-warm-50 hover:text-warm-900 transition-colors whitespace-nowrap"
                >
                  <i className={`${cat.icon} text-tealwarm-600 text-base`}></i>
                  {cat.name}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-warm-100 max-h-[80vh] overflow-y-auto">
          <div className="px-6 py-4 space-y-1">
            <Link to="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 py-3 text-sm font-semibold text-warm-900">
              <div className="w-8 h-8 rounded-lg bg-tealwarm-50 flex items-center justify-center text-tealwarm-600">
                <i className="ri-home-4-line"></i>
              </div>
              Home
            </Link>
            <a href="/#categorie" onClick={(e) => { e.preventDefault(); window.location.href = '/#categorie'; setMobileMenuOpen(false); }} className="flex items-center gap-3 py-3 text-sm font-medium text-warm-700 w-full text-left cursor-pointer">
              <div className="w-8 h-8 rounded-lg bg-cream-100 flex items-center justify-center text-warm-600">
                <i className="ri-grid-line"></i>
              </div>
              Categorie
            </a>
            <Link to="/itaobuy-spreadsheet-guida-completa" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 py-3 text-sm font-medium text-warm-700">
              <div className="w-8 h-8 rounded-lg bg-warm-100 flex items-center justify-center text-warm-600">
                <i className="ri-book-open-line"></i>
              </div>
              Guida Acquisti
            </Link>
            <a href="/#faq" onClick={(e) => { e.preventDefault(); window.location.href = '/#faq'; setMobileMenuOpen(false); }} className="flex items-center gap-3 py-3 text-sm font-medium text-warm-700 w-full text-left cursor-pointer">
              <div className="w-8 h-8 rounded-lg bg-tealwarm-50 flex items-center justify-center text-tealwarm-600">
                <i className="ri-question-line"></i>
              </div>
              FAQ
            </a>
          </div>

          <div className="border-t border-warm-100 px-6 py-4">
            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="flex items-center justify-between w-full py-2 text-sm font-semibold text-warm-900 cursor-pointer bg-transparent border-none"
            >
              <span className="flex items-center gap-2">
                <i className="ri-shopping-bag-3-line text-tealwarm-600"></i>
                Categorie Prodotto
              </span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 text-warm-400 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {mobileCatOpen && (
              <div className="mt-2 space-y-4">
                {categoryGroups.map((group) => (
                  <div key={group.title}>
                    <p className="text-xs font-semibold uppercase tracking-wide text-warm-400 mb-2 px-1">
                      {group.title}
                    </p>
                    <div className="space-y-0.5">
                      {group.items.map((cat) => (
                        <a
                          key={cat.id}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center gap-3 py-2.5 px-3 rounded-xl text-sm text-warm-600 hover:bg-cream-50 hover:text-warm-900 transition-colors"
                        >
                          <div className="w-7 h-7 rounded-lg bg-tealwarm-50 flex items-center justify-center text-tealwarm-600 flex-shrink-0">
                            <i className={`${cat.icon} text-xs`}></i>
                          </div>
                          <span>{cat.name}</span>
                          <i className="ri-arrow-right-up-line text-warm-300 ml-auto text-xs"></i>
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-warm-100 px-6 py-4 flex gap-2">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileMenuOpen(false)}
              className="flex-1 text-center px-4 py-3 rounded-full bg-warm-600 text-white text-sm font-semibold whitespace-nowrap"
            >
              Itaobuy Sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileMenuOpen(false)}
              className="flex-1 text-center px-4 py-3 rounded-full bg-warm-900 text-white text-sm font-semibold whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}